import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getClub, joinClub, inquireClub, getMembershipPricing, createMembershipCheckout, getClubUpcomingRides, ClubRide } from '../../services/clubService';
import { trackClubView } from '../../utils/clubStoreAnalytics';
import { Club, MembershipPricing } from '../../types/clubs';
import toast from 'react-hot-toast';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, MapPinIcon, UserGroupIcon, PhoneIcon, EnvelopeIcon, GlobeAltIcon, SparklesIcon, CalendarIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, ClockIcon } from '@heroicons/react/24/solid';

// Activity labels for display
const ACTIVITY_LABELS: Record<string, string> = {
  group_rides: 'Group Rides',
  training_rides: 'Training Rides',
  virtual_training: 'Virtual Training (Zwift, etc.)',
  competitions: 'Races & Competitions',
  social_events: 'Social Events',
  skill_clinics: 'Skill Clinics & Workshops',
  bike_touring: 'Bike Touring & Travel',
  charity_rides: 'Charity & Fundraiser Rides',
  youth_programs: 'Youth Programs',
  maintenance_workshops: 'Maintenance Workshops',
};

const ClubDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { clubId } = useParams<{ clubId: string }>();
  const navigate = useNavigate();
  const [club, setClub] = useState<Club | null>(null);
  const [upcomingRides, setUpcomingRides] = useState<ClubRide[]>([]);
  const [pricing, setPricing] = useState<MembershipPricing | null>(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [showInquireModal, setShowInquireModal] = useState(false);
  const [inquiryMessage, setInquiryMessage] = useState('');
  const [submittingInquiry, setSubmittingInquiry] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (clubId) {
      loadClubDetails();
    }
  }, [clubId]);

  useEffect(() => {
    if (club && user?.id) {
      trackClubView(club.id, 'ClubDetails', user.role || undefined).catch(console.error);
    }
  }, [club, user?.id]);

  useEffect(() => {
    // Load pricing if membership is enabled
    if (club?.membershipEnabled && !pricing && clubId) {
      loadPricing();
    }
  }, [club?.membershipEnabled, clubId]);

  const loadClubDetails = async () => {
    if (!clubId) return;
    
    try {
      setError(null);
      const [clubData, ridesData] = await Promise.all([
        getClub(clubId),
        getClubUpcomingRides(clubId),
      ]);
      setClub(clubData);
      setUpcomingRides(ridesData);
    } catch (err: any) {
      console.error('Error fetching club details:', err);
      setError(err.message || t('common.error') || 'Failed to load club details');
      toast.error(err.message || t('common.error') || 'Failed to load club details');
      navigate('/discover/clubs');
    } finally {
      setLoading(false);
    }
  };
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const loadPricing = async () => {
    if (!clubId) return;
    
    try {
      const pricingData = await getMembershipPricing(clubId);
      setPricing(pricingData);
    } catch (err: any) {
      console.error('Error fetching pricing:', err);
      // Non-critical, don't show error
    }
  };

  const handleJoin = async () => {
    if (!club || !user || !clubId) return;

    setJoining(true);
    try {
      await joinClub(clubId);
      toast.success(t('clubs.joinedSuccessfully') || 'You have successfully joined the club!');
      loadClubDetails();
    } catch (err: any) {
      toast.error(err.message || t('clubs.joinFailed') || 'Failed to join club');
    } finally {
      setJoining(false);
    }
  };

  const handleInquire = async () => {
    if (!inquiryMessage.trim() || !clubId) {
      toast.error(t('clubs.inquiryMessageRequired') || 'Please enter an inquiry message');
      return;
    }

    setSubmittingInquiry(true);
    try {
      await inquireClub(clubId, inquiryMessage.trim());
      setShowInquireModal(false);
      setInquiryMessage('');
      toast.success(t('clubs.inquirySent') || 'Your inquiry has been sent to the club administrator.');
      loadClubDetails();
    } catch (err: any) {
      toast.error(err.message || t('clubs.inquiryFailed') || 'Failed to send inquiry');
    } finally {
      setSubmittingInquiry(false);
    }
  };

  const handleJoinWithPayment = async () => {
    if (!club || !user || !clubId) return;

    setProcessingPayment(true);
    try {
      const { url } = await createMembershipCheckout(clubId, selectedPeriod);
      // Open Stripe checkout URL in new window
      window.open(url, '_blank');
      toast.success(t('clubs.redirectingToPayment') || 'Redirecting to payment...');
    } catch (err: any) {
      toast.error(err.message || t('clubs.paymentFailed') || 'Failed to initiate payment');
    } finally {
      setProcessingPayment(false);
    }
  };

  const getMembershipStatus = () => {
    if (!club?.membership) return null;
    
    const status = club.membership.status;
    if (status === 'ACTIVE') {
      return { label: t('clubs.member') || 'Member', color: 'text-green-600 bg-green-50', icon: CheckCircleIcon };
    } else if (status === 'PENDING') {
      return { label: t('clubs.pendingApproval') || 'Pending Approval', color: 'text-orange-600 bg-orange-50', icon: ClockIcon };
    } else if (status === 'REJECTED') {
      return { label: t('clubs.rejected') || 'Rejected', color: 'text-red-600 bg-red-50', icon: XMarkIcon };
    }
    return null;
  };

  const membershipStatus = getMembershipStatus();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">{t('common.loading') || 'Loading club details...'}</div>
      </div>
    );
  }

  if (error || !club) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || t('clubs.clubNotFound') || 'Club not found'}</p>
          <button
            onClick={loadClubDetails}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 mr-2"
          >
            {t('common.retry') || 'Retry'}
          </button>
          <button
            onClick={() => navigate('/discover/clubs')}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            {t('common.back') || 'Back to Clubs'}
          </button>
        </div>
      </div>
    );
  }

  const showJoinButton = !club.isMember && !membershipStatus;
  const showPaymentFlow = showJoinButton && club.autoEnrollment && club.membershipEnabled && pricing;
  const showInquireButton = showJoinButton && !club.autoEnrollment;
  const showDirectJoinButton = showJoinButton && club.autoEnrollment && !club.membershipEnabled;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{club.name}</h1>
            {club.location && (
              <div className="flex items-center text-gray-600 mb-2">
                <MapPinIcon className="h-5 w-5 mr-2" />
                <span>{club.location}</span>
              </div>
            )}
          </div>
          {club.isActive && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              {t('common.active') || 'Active'}
            </span>
          )}
        </div>

        {membershipStatus && (
          <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${membershipStatus.color}`}>
            <membershipStatus.icon className="h-5 w-5 mr-2" />
            <span>{membershipStatus.label}</span>
          </div>
        )}
      </div>

      {/* Description */}
      {club.description && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('clubs.about') || 'About'}</h2>
          <p className="text-gray-700 whitespace-pre-wrap">{club.description}</p>
        </div>
      )}

      {/* Activities/Services Section - Between About and Information */}
      {club.activities && club.activities.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <SparklesIcon className="h-6 w-6 mr-2 text-primary-600" />
            {t('clubs.services') || 'Activities & Services'}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {club.activities.map((activity) => (
              <div
                key={activity}
                className="flex items-center p-3 bg-gray-50 rounded-lg"
              >
                <span className="text-primary-600 mr-2">•</span>
                <span className="text-gray-700">{ACTIVITY_LABELS[activity] || activity}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Membership Pricing (always visible for non-members) */}
      {!club.isMember && (club.membershipFeeMonthly || club.membershipFeeAnnual) && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <CurrencyDollarIcon className="h-6 w-6 mr-2 text-primary-600" />
            {t('clubs.membershipPricing') || 'Membership Pricing'}
          </h2>
          <div className="grid grid-cols-2 gap-4">
            {club.membershipFeeMonthly && (
              <div className="p-4 bg-gray-50 rounded-lg text-center">
                <div className="text-sm text-gray-600 mb-1">{t('clubs.monthly') || 'Monthly'}</div>
                <div className="text-2xl font-bold text-primary-600">
                  ${Number(club.membershipFeeMonthly).toFixed(2)}
                </div>
                <div className="text-xs text-gray-500">{t('clubs.perMonth') || '/month'}</div>
              </div>
            )}
            {club.membershipFeeAnnual && (
              <div className="p-4 bg-gray-50 rounded-lg text-center">
                <div className="text-sm text-gray-600 mb-1">{t('clubs.annual') || 'Annual'}</div>
                <div className="text-2xl font-bold text-primary-600">
                  ${Number(club.membershipFeeAnnual).toFixed(2)}
                </div>
                <div className="text-xs text-gray-500">{t('clubs.perYear') || '/year'}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Club Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('clubs.information') || 'Information'}</h2>
        
        <div className="space-y-4">
          {club.city && (
            <div className="flex items-start">
              <MapPinIcon className="h-5 w-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <div className="text-sm text-gray-500">{t('clubs.city') || 'City'}</div>
                <div className="text-base text-gray-900">{club.city}</div>
              </div>
            </div>
          )}

          {club.address && (
            <div className="flex items-start">
              <MapPinIcon className="h-5 w-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <div className="text-sm text-gray-500">{t('clubs.address') || 'Address'}</div>
                <div className="text-base text-gray-900">{club.address}</div>
              </div>
            </div>
          )}

          {club.memberCount !== undefined && (
            <div className="flex items-start">
              <UserGroupIcon className="h-5 w-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <div className="text-sm text-gray-500">{t('clubs.members') || 'Members'}</div>
                <div className="text-base text-gray-900">{club.memberCount}</div>
              </div>
            </div>
          )}

          {club.phone && (
            <div className="flex items-start">
              <PhoneIcon className="h-5 w-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm text-gray-500">{t('clubs.phone') || 'Phone'}</div>
                <a
                  href={`tel:${club.phone}`}
                  className="text-base text-primary-600 hover:text-primary-700 hover:underline"
                >
                  {club.phone}
                </a>
              </div>
            </div>
          )}

          {club.email && (
            <div className="flex items-start">
              <EnvelopeIcon className="h-5 w-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm text-gray-500">{t('clubs.email') || 'Email'}</div>
                <a
                  href={`mailto:${club.email}`}
                  className="text-base text-primary-600 hover:text-primary-700 hover:underline"
                >
                  {club.email}
                </a>
              </div>
            </div>
          )}

          {club.website && (
            <div className="flex items-start">
              <GlobeAltIcon className="h-5 w-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm text-gray-500">{t('clubs.website') || 'Website'}</div>
                <a
                  href={club.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-base text-primary-600 hover:text-primary-700 hover:underline"
                >
                  {club.website}
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Membership Payment Flow */}
      {showPaymentFlow && pricing && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('clubs.membershipPricing') || 'Membership Pricing'}</h2>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <button
              onClick={() => setSelectedPeriod('monthly')}
              className={`p-4 border-2 rounded-lg text-center ${
                selectedPeriod === 'monthly'
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="text-sm font-medium text-gray-700 mb-1">{t('clubs.monthly') || 'Monthly'}</div>
              {pricing.club.pricing.monthly !== null ? (
                <div className="text-lg font-bold text-primary-600">
                  ${(pricing.club.pricing.monthly / 100).toFixed(2)} {t('clubs.perMonth') || '/month'}
                </div>
              ) : (
                <div className="text-sm text-gray-400 italic">{t('clubs.notAvailable') || 'Not Available'}</div>
              )}
            </button>

            <button
              onClick={() => setSelectedPeriod('annual')}
              className={`p-4 border-2 rounded-lg text-center ${
                selectedPeriod === 'annual'
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="text-sm font-medium text-gray-700 mb-1">{t('clubs.annual') || 'Annual'}</div>
              {pricing.club.pricing.annual !== null ? (
                <div className="text-lg font-bold text-primary-600">
                  ${(pricing.club.pricing.annual / 100).toFixed(2)} {t('clubs.perYear') || '/year'}
                </div>
              ) : (
                <div className="text-sm text-gray-400 italic">{t('clubs.notAvailable') || 'Not Available'}</div>
              )}
            </button>
          </div>

          <button
            onClick={handleJoinWithPayment}
            disabled={processingPayment || (selectedPeriod === 'monthly' && pricing.club.pricing.monthly === null) || (selectedPeriod === 'annual' && pricing.club.pricing.annual === null)}
            className="w-full px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {processingPayment 
              ? (t('clubs.processing') || 'Processing...')
              : (t('clubs.joinWithPayment') || 'Join with Payment')}
          </button>
        </div>
      )}

      {/* Action Buttons */}
      {showDirectJoinButton && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <button
            onClick={handleJoin}
            disabled={joining}
            className="w-full px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {joining ? (t('common.loading') || 'Loading...') : (t('clubs.joinClub') || 'Join Club')}
          </button>
        </div>
      )}

      {showInquireButton && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <button
            onClick={() => setShowInquireModal(true)}
            className="w-full px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            {t('clubs.sendInquiry') || 'Send Inquiry'}
          </button>
        </div>
      )}

      {/* Upcoming Rides Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <CalendarIcon className="h-6 w-6 mr-2 text-primary-600" />
          {t('clubs.upcomingRides') || 'Upcoming Rides'}
        </h2>
        {upcomingRides.length > 0 ? (
          <div className="space-y-4">
            {upcomingRides.map((ride) => (
              <Link
                key={ride.id}
                to={`/rides/${ride.id}`}
                className="block p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium text-gray-900">{ride.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {formatDate(ride.departureDate)}
                      {ride.departureTime && ` at ${ride.departureTime}`}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">{ride.departureLocation}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-primary-600">{ride.distanceKm} km</div>
                    <div className="flex items-center text-sm text-gray-500 mt-1">
                      <UserGroupIcon className="h-4 w-4 mr-1" />
                      {ride._count.participants}/{ride.maxRiders}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">
            {t('clubs.noUpcomingRides') || 'No upcoming rides'}
          </p>
        )}
      </div>

      {/* Inquiry Modal */}
      <Dialog open={showInquireModal} onClose={() => setShowInquireModal(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-lg rounded-lg bg-white p-6 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <Dialog.Title className="text-xl font-semibold text-gray-900">
                {t('clubs.sendInquiry') || 'Send Inquiry'}
              </Dialog.Title>
              <button
                onClick={() => setShowInquireModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              {t('clubs.inquiryDescription') || 'Send a message to the club administrator to request membership.'}
            </p>

            <textarea
              value={inquiryMessage}
              onChange={(e) => setInquiryMessage(e.target.value)}
              placeholder={t('clubs.message') || 'Message'}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent mb-4"
            />

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowInquireModal(false);
                  setInquiryMessage('');
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                {t('common.cancel') || 'Cancel'}
              </button>
              <button
                onClick={handleInquire}
                disabled={submittingInquiry || !inquiryMessage.trim()}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {submittingInquiry ? (t('common.loading') || 'Loading...') : (t('common.send') || 'Send')}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
};

export default ClubDetailsPage;
